import { useState, useEffect } from "react";
// reCAPTCHA (Login): when restoring, add useRef — import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
// ---------------------------------------------------------------------------
// Google reCAPTCHA (Login) — temporarily disabled. To restore: uncomment the
// import below, then uncomment all blocks marked with "reCAPTCHA (Login)".
// ---------------------------------------------------------------------------
// import ReCAPTCHA from "react-google-recaptcha";
import { Helmet } from "react-helmet";
import { updateCurrentStudioLastActivity } from "../utils/activityTracker";
import PaymentExpirationNotification from "../components/PaymentExpirationNotification";
import AuthPageLayout from "../components/auth/AuthPageLayout";
import { HoneypotField } from "../components/HoneypotField";
import {
  HONEYPOT_FIELD_NAME,
  isHoneypotTriggered,
} from "../constants/formHoneypot";

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL || process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Supabase URL and Anon Key must be set in environment variables."
  );
}
const supabase = createClient(supabaseUrl, supabaseAnonKey);

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | React.ReactElement | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [lastAttempt, setLastAttempt] = useState(0);
  const LOCKOUT_KEY = "login_lockout_until";
  const ATTEMPTS_KEY = "login_attempts";
  const LAST_ATTEMPT_KEY = "login_last_attempt";
  const MAX_ATTEMPTS = 5;
  const LOCKOUT_DURATION = 5 * 60 * 1000; // 5 minutes in ms
  const MIN_ATTEMPT_INTERVAL = 1000; // 1 second between attempts
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [showPaymentNotification, setShowPaymentNotification] = useState(false);

  // Add CSRF protection
  const [csrfToken, setCsrfToken] = useState<string>("");

  // reCAPTCHA (Login) — state/ref (restore with import)
  // const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  // const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [honeypot, setHoneypot] = useState("");

  // Generate CSRF token on component mount
  useEffect(() => {
    const generateCSRFToken = () => {
      const token = crypto.randomUUID();
      setCsrfToken(token);
      setStoredData("csrf_token", token);
      return token;
    };

    // Check if we already have a valid token
    const existingToken = getStoredData("csrf_token");
    if (existingToken) {
      setCsrfToken(existingToken);
    } else {
      generateCSRFToken();
    }
  }, []);

  // Validate origin to prevent CSRF attacks
  const validateOrigin = () => {
    // For login forms, we need to be more permissive to allow direct access
    // The CSRF token and reCAPTCHA provide sufficient protection
    return true;
  };

  // Validate CSRF token
  const validateCSRFToken = (token: string) => {
    // Since we have reCAPTCHA protection, we can be more lenient with CSRF
    // This helps avoid issues with session storage across different scenarios
    const storedToken = getStoredData("csrf_token");
    // If we have a token and it matches, great. If not, allow anyway since reCAPTCHA protects us
    if (!storedToken || !token) {
      return true; // Be permissive if token isn't properly set
    }
    return token === storedToken;
  };

  // Use sessionStorage instead of localStorage for better security
  const getStoredData = (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const setStoredData = (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Fallback: if sessionStorage fails, use memory-only storage
    }
  };

  const clearStoredData = (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  };

  // Add input validation and sanitization functions
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const sanitizeInput = (input: string) => {
    return input.replace(/[<>]/g, "").trim();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedEmail = sanitizeInput(e.target.value);
    setEmail(sanitizedEmail);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric characters for the code
    const sanitizedCode = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
    setCode(sanitizedCode);
  };

  useEffect(() => {
    // On mount, check sessionStorage for lockout and attempts
    const storedLockout = getStoredData(LOCKOUT_KEY);
    const storedAttempts = getStoredData(ATTEMPTS_KEY);
    const storedLastAttempt = getStoredData(LAST_ATTEMPT_KEY);

    if (storedLockout) {
      const lockoutTime = Number(storedLockout);
      if (Date.now() < lockoutTime) {
        setLockoutUntil(lockoutTime);
      } else {
        // Lockout expired, clear stored data
        clearStoredData(LOCKOUT_KEY);
        clearStoredData(ATTEMPTS_KEY);
        clearStoredData(LAST_ATTEMPT_KEY);
        setLockoutUntil(null);
        setAttempts(0);
        setLastAttempt(0);
      }
    }

    if (storedAttempts) {
      setAttempts(Number(storedAttempts));
    }

    if (storedLastAttempt) {
      setLastAttempt(Number(storedLastAttempt));
    }
  }, []);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    // CSRF protection
    if (!validateOrigin()) {
      setError("Invalid request origin. Please refresh the page.");
      return;
    }

    if (!validateCSRFToken(csrfToken)) {
      setError("Invalid security token. Please refresh the page.");
      return;
    }

    // Check if user is currently locked out
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / 60000);
      setError(
        `Too many failed attempts. Please try again in ${remainingTime} minute(s).`
      );
      return;
    }

    // Rate limiting - prevent rapid-fire attempts
    const now = Date.now();
    if (now - lastAttempt < MIN_ATTEMPT_INTERVAL) {
      setError("Please wait a moment before trying again.");
      return;
    }

    // reCAPTCHA (Login) — validation (restore with state/ref + widget)
    // if (!recaptchaValue && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
    //   setError("Please complete the reCAPTCHA verification");
    //   return;
    // }

    setLoading(true);
    setError(null);
    setLastAttempt(now);
    setStoredData(LAST_ATTEMPT_KEY, now.toString());

    // Validate email format
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    if (isHoneypotTriggered(honeypot)) {
      setError("Unable to process request. Please try again.");
      setLoading(false);
      return;
    }

    try {
      // First, check if the email exists in the database via backend API
      try {
  
        
        const checkResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/check-email-exists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, [HONEYPOT_FIELD_NAME]: honeypot }),
        });

        if (!checkResponse.ok) {
          if (checkResponse.status === 400) {
            const errData = await checkResponse.json().catch(() => ({}));
            setError(
              (errData as { error?: string }).error ||
                "Unable to process request. Please try again."
            );
            setLoading(false);
            return;
          }
        }

        if (checkResponse.ok) {
          const responseData = await checkResponse.json();
          
          
          const { exists } = responseData;
          
          if (!exists) {

            setError(
              <span>
                No account found with this email address.{" "}
                <Link to="/signup" className="auth-link">
                  Create a new account
                </Link>
              </span>
            );
            setLoading(false);
            return;
          } else {
  
          }
        } else {
          
        }
      } catch (checkError) {
        // If email check fails, proceed with OTP (fail-safe)
        console.warn("Could not verify email existence, proceeding with OTP:", checkError);
      }

      // If email exists or we couldn't verify, proceed with OTP
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) {
        // Increment failed attempts
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setStoredData(ATTEMPTS_KEY, newAttempts.toString());

        // Check if we should lock out the user
        if (newAttempts >= MAX_ATTEMPTS) {
          const lockoutTime = Date.now() + LOCKOUT_DURATION;
          setLockoutUntil(lockoutTime);
          setStoredData(LOCKOUT_KEY, lockoutTime.toString());
          setError("Too many failed attempts. Please try again in 5 minutes.");
        } else {
          throw error;
        }
      } else {
        // Reset attempts on successful code send
        setAttempts(0);
        clearStoredData(ATTEMPTS_KEY);
        clearStoredData(LOCKOUT_KEY);
        clearStoredData(LAST_ATTEMPT_KEY);
        setLockoutUntil(null);
        setLastAttempt(0);
        setCodeSent(true);

        // reCAPTCHA (Login) — reset after successful send (restore with ref)
        // setRecaptchaValue(null);
        // if (recaptchaRef.current) {
        //   recaptchaRef.current.reset();
        // }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    // CSRF protection
    if (!validateOrigin()) {
      setError("Invalid request origin. Please refresh the page.");
      return;
    }

    if (!validateCSRFToken(csrfToken)) {
      setError("Invalid security token. Please refresh the page.");
      return;
    }

    // Check if user is currently locked out
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / 60000);
      setError(
        `Too many failed attempts. Please try again in ${remainingTime} minute(s).`
      );
      return;
    }

    // Rate limiting - prevent rapid-fire attempts
    const now = Date.now();
    if (now - lastAttempt < MIN_ATTEMPT_INTERVAL) {
      setError("Please wait a moment before trying again.");
      return;
    }

    setLoading(true);
    setError(null);
    setLastAttempt(now);
    setStoredData(LAST_ATTEMPT_KEY, now.toString());

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: "email",
      });
      if (error) {
        // Increment failed attempts
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setStoredData(ATTEMPTS_KEY, newAttempts.toString());

        // Check if we should lock out the user
        if (newAttempts >= MAX_ATTEMPTS) {
          const lockoutTime = Date.now() + LOCKOUT_DURATION;
          setLockoutUntil(lockoutTime);
          setStoredData(LOCKOUT_KEY, lockoutTime.toString());
          setError("Too many failed attempts. Please try again in 5 minutes.");
        } else {
          throw error;
        }
      } else {
        // Reset attempts on successful login
        setAttempts(0);
        clearStoredData(ATTEMPTS_KEY);
        clearStoredData(LOCKOUT_KEY);
        clearStoredData(LAST_ATTEMPT_KEY);
        setLockoutUntil(null);
        setLastAttempt(0);
        
        // Update last_activity timestamp in studios table (session is available after verifyOtp)
        try {
          const { data: { session } } = await supabase.auth.getSession();
          await updateCurrentStudioLastActivity(session?.access_token ?? null);
        } catch (error) {
          console.error('Failed to update last_activity:', error);
          // Don't block login if this fails
        }
        
        // Show payment expiration notification after successful login
        setShowPaymentNotification(true);
        
        // Navigate to app after a short delay to allow notification to show
        setTimeout(() => {
          navigate("/app");
        }, 100);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to log in");
    } finally {
      setLoading(false);
    }
  };

  // Calculate lockout feedback
  let lockoutFeedback = null;
  if (lockoutUntil && Date.now() < lockoutUntil) {
    const remainingTime = Math.ceil((lockoutUntil - Date.now()) / 60000);
    lockoutFeedback = `Account locked. Try again in ${remainingTime} minute(s).`;
  }

  const loginTitle = codeSent ? "Check Your Email" : "Welcome Back";

  const loginSubtitle = codeSent ? (
    <div className="auth-verify-notice">
      We sent a login code to <strong>{email}</strong>
      <br />
      <span className="auth-verify-notice-spam">
        Don&apos;t see it? Check your spam or junk folder, then mark it as
        not spam.
      </span>
    </div>
  ) : (
    "Sign in to manage your martial arts studio and students."
  );

  return (
    <>
      <Helmet>
        <title>Login to Your Martial Arts Studio Account | DojoTrack</title>
        <meta name="description" content="Sign in to your DojoTrack account to manage your martial arts studio, track students, and access your dojo management tools." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <PaymentExpirationNotification
        showOnLogin={true}
        onClose={() => setShowPaymentNotification(false)}
      />
      <AuthPageLayout
        title={loginTitle}
        subtitle={loginSubtitle}
        centerHeader
        footerLink={
          !codeSent ? (
            <>
              Don&apos;t have an account?{" "}
              <Link to="/signup" className="auth-link">
                Get started for free
              </Link>
            </>
          ) : undefined
        }
      >
        {!codeSent ? (
          <form className="space-y-5" onSubmit={handleSendCode}>
            <HoneypotField value={honeypot} onChange={setHoneypot} />
            <div>
              <label htmlFor="email-address" className="auth-label">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="auth-input"
                placeholder="you@yourstudio.com"
                value={email}
                onChange={handleEmailChange}
              />
            </div>
            {(error || lockoutFeedback) && (
              <div className="auth-alert auth-alert-error">
                {lockoutFeedback || error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary"
            >
              {loading ? "Sending code..." : "Send Login Code"}
            </button>
          </form>
        ) : (
          <form className="space-y-5" onSubmit={handleVerifyCode}>
            <div>
              <label htmlFor="code" className="auth-label">
                One-time code
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                className="auth-input"
                placeholder="Enter the code from your email"
                value={code}
                onChange={handleCodeChange}
              />
            </div>
            {(error || lockoutFeedback) && (
              <div className="auth-alert auth-alert-error">
                {lockoutFeedback || error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary"
            >
              {loading ? "Verifying..." : "Verify Code & Login"}
            </button>
            <button
              type="button"
              className="auth-btn-secondary"
              onClick={() => {
                setCodeSent(false);
                setCode("");
              }}
            >
              Back to email entry
            </button>
          </form>
        )}
      </AuthPageLayout>
    </>
  );
}
